import i18next from 'i18next'
import uiStore from '../stores/uiStore'
import alertStore, { alert } from '../stores/alertStore'
import sessionStore from '../stores/sessionStore'
import { PodClass } from '../classes/Pod'
import { Op, idbStoredOp } from '../shared/src/types/Ops'
import { syncPullRequest } from '../shared/src/types/Pod'
import { versions } from '../version'
import { environment } from '../environment'

const version = versions[0]

type IdpDescriptor = { name:string, loginEndpoint:string, description:string }

export const env:{
  frontendUrl: string,
  shortnameRedirectUrl: string,
  backendBaseUrl: string,
  backendPdfUrl: string,
  dev: boolean,
  httpDefaultTimeout: number,
  idps: IdpDescriptor[]

} = {
  frontendUrl:             (typeof environment.frontendUrl !== 'undefined') ? environment.frontendUrl                   : 'http://localhost:3000',
  shortnameRedirectUrl:    (typeof environment.shortnameRedirectUrl !== 'undefined') ? environment.shortnameRedirectUrl : 'http://localhost:5000/resolvePodId',
  backendBaseUrl:          (typeof environment.backendBaseUrl !== 'undefined') ? environment.backendBaseUrl             : 'http://localhost:5000',
  backendPdfUrl:           (typeof environment.backendPdfUrl !== 'undefined') ? environment.backendPdfUrl               : 'http://localhost:5100',
  dev:                     (typeof environment.dev !== 'undefined') ? environment.dev                                   : true,
  httpDefaultTimeout:      (typeof environment.httpDefaultTimeout !== 'undefined') ? environment.httpDefaultTimeout     : 8000,
  idps:                    (typeof environment.idps !== 'undefined') ? environment.idps                                 : [{
                                                                                                                          name: 'keycloak',
                                                                                                                          loginEndpoint: 'login',
                                                                                                                          description: '',
                                                                                                                        }]
}

export const baseUrl = environment.backendBaseUrl
export const maxOpsSyncUpstreamPerRequest = 100

const api = {
  /** redirect to server login route,
   *  wait for the callback from the server, which will set a cookie with the sessionId
   */
  async login(provider:string, credentials:object|null = null) {
    const providerInfo = environment.idps.find((idp:any)=>idp.name===provider)
    if (providerInfo) {
      if (providerInfo.loginEndpoint === '*local') {
        const res = await this.fetch(`${baseUrl}/auth/callback_local`, {
          method:'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(credentials)
        })
        if (res.status === 200) return res.body
        return false
      }
      else {
        const location = window.location.href
        localStorage.setItem('LoginDestination', location)
        window.location.href = `${baseUrl}/${providerInfo.loginEndpoint}?target=${encodeURI(location)}`
      }
    }
    else {
      console.log(`Unknown provider ${provider}`)
      return false
    }
    await new Promise((resolve) => {
      setTimeout(resolve, 5000)
    })
    return true
  },

  async pdfUpload(podId:string, nodeId:string) {
    const sessionId = sessionStore.session ? sessionStore.session.sessionId : null
    const res = await this.fetch(`${baseUrl}/uploadPdf`, {
      method:'POST',
      headers: {
        'X-SHRIMP-ID': sessionId,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
          podId,
          nodeId,
        })
    })
    return res
  },

  async checkPodIdHr(podIdHr:string) {
    const sessionId = sessionStore.session ? sessionStore.session.sessionId : null
    const res = await this.fetch(`${env.backendBaseUrl}/live/checkPodIdHr`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-SHRIMP-ID': sessionId,
      },
      body: JSON.stringify({
        podIdHr,
      })
    })

    if ((res.status === 200) && (res.body.result.cnt === 0)) return true
    if ((res.status === 200) && (res.body.result.cnt>0)) return false
    return undefined
  },

  async getPodInformation(podId:string) {
    const sessionId = sessionStore.session ? sessionStore.session.sessionId : null
    const res = await this.fetch(`${env.backendBaseUrl}/live/podInfo`, {
      method: 'POST',
      headers: {
        'X-SHRIMP-ID': sessionId,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        podId
      })
    })
    if(res.status === 200 && res.body) {
      return res.body
    }
    return null
  },

  async getPodActivity(podId: string, before:number=0, doCache:boolean=false) {
    const sessionId = sessionStore.session ? sessionStore.session.sessionId : null
    const res = await this.fetch(`${baseUrl}/activity`, {
      method:'POST',
      headers: {
        'X-SHRIMP-ID': sessionId,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
       podId,
       before: before ? before : null,
       doCache
      })
    })
    return res
  },

  async reportError(error: Error|string, info: { componentStack?: string|null|undefined, context?: string, url?:string } | string) {
    const sessionId = sessionStore.session ? sessionStore.session.sessionId : null
    const userId = sessionStore.session ? sessionStore.session.user.userId : null
    return this.fetch(`${baseUrl}/reportError`, {
      method:'POST',
      headers: {
        'X-SHRIMP-ID': sessionId,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        version: version.version,
        report: {
          error,
          info,
        }
      })
    })
  },

  async resetCache(cacheSelection:object) {
    this.fetch(`${baseUrl}/resetCache`, {
      method:'post',
      body: JSON.stringify(cacheSelection)
    })
  },

  async syncFull(pulling: Array<syncPullRequest>, pushing: Op[] | idbStoredOp[], sessionId: string, clientLastSyncOid: number) {
    //if (sessionId === '') sessionId = sessionStore.session ? sessionStore.session.sessionId : ""
    const res = await this.fetch(`${baseUrl}/syncFull`, {
      method: 'POST',
      timeout: env.httpDefaultTimeout,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-SHRIMP-ID': sessionId,
      },
      body: JSON.stringify({
        pulling: pulling,
        pushing: pushing,
        clientLastSyncOid: clientLastSyncOid // sessionStore.session.clientLastSyncOid
      })
    })
    if (res) return res
    return undefined
  },

  async getPendingOps(podId: string|null = null) {
    const res = await this.fetch(`${baseUrl}/getPendingOps`, {
      method: 'POST',
      timeout: env.httpDefaultTimeout,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-SHRIMP-ID': sessionStore.session ? sessionStore.session.sessionId : "",
      },
      body: JSON.stringify({podId})
    })
    if (res) return res.body as idbStoredOp[]
    return undefined
  },


  async getPodFingerprint(podId: string, hashed:boolean = true, fromBackend:boolean = false) {
    const res = await this.fetch(`${baseUrl}/getPodFingerprint`, {
      method: 'POST',
      timeout: env.httpDefaultTimeout,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-SHRIMP-ID': sessionStore.session ? sessionStore.session.sessionId : "",
      },
      body: JSON.stringify({podId, hashed, fromBackend})
    })
    if (res) return res.body
    return undefined
  },

  async reportDivergentFingerprint(podId: string, sessionId:string, fingerprint:string) {
    const res = await this.fetch(`${baseUrl}/getPodFingerprint`, {
      method: 'POST',
      timeout: env.httpDefaultTimeout,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-SHRIMP-ID': sessionId,
      },
      body: JSON.stringify({podId, hashed:false, fromBackend:false, clientFingerprint: fingerprint})
    })
    if (res.status === 200) console.log(`Reported divergent fingerprint for debugging`); else console.warn(`reportDivergentFingerprint failed: `, res)
  },

  async submitUserFeedback(sessionId:string, data:object) {
    const res = await this.fetch(`${baseUrl}/live/submitUserFeedback`, {
      method: 'POST',
      timeout: env.httpDefaultTimeout,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-SHRIMP-ID': sessionId,
      },
      body: JSON.stringify({data})
    })
    return res
  },

  async upgradeUser(sessionId:string, data:object) {
    const res = await this.fetch(`${baseUrl}/live/upgradeUser`, {
      method: 'POST',
      timeout: env.httpDefaultTimeout,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-SHRIMP-ID': sessionId,
      },
      body: JSON.stringify({data})
    })
    return res
  },

  /** attempt to load a pod and receive either a fully populated pod or an empty
   *  init pod, which is then populated through ops during the chunked loading
   *  cycles
   */

  async loadPod(podId: string, reset: boolean = false) {
    const obj = await this.fetch(`${baseUrl}/loadPod`, {
      method: 'POST',
      timeout: env.httpDefaultTimeout,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-SHRIMP-ID': sessionStore.session ? sessionStore.session.sessionId : "",
      },
      body: JSON.stringify({ podId, reset })
    })

    if ((obj) && (obj.body?.pod)) {
      if (!obj.body?.pod?.tools?.length) podId === '4gP.1.1.3Db1-6' ? obj.body.pod.tools = ['marker', 'box', 'pencil', 'highlighter'] : obj.body.pod.tools = ['marker', 'box']
      const pod = new PodClass(obj.body.pod, true)
      return pod
    }
    return undefined
  },

  /** load a server-determined number of operations that will populate an originally empty
   *  init pod to the extent defined by initMaxCoid.
   */
  async loadPodChunk(podId: string, lastSyncOid: number, initMaxCoid: number) {
    const res = await this.fetch(`${baseUrl}/loadPodChunk`, {
      method: 'POST',
      timeout: env.httpDefaultTimeout,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-SHRIMP-ID': sessionStore.session ? sessionStore.session.sessionId : "",
      },
      body: JSON.stringify({ podId, lastSyncOid, initMaxCoid })
    })

    if (res.ok) {
      const { ops, totalOps } = res.body
      return {
        ops: ops as Op[],
        totalOps: totalOps as number,
      }
    }

    return undefined
  },

  /** request the service worker whether an active session exists and can be returned
   *  the cookie sent with the request is used for authentication,
   *  this cookie is deleted from the server after the request is made
   *  if no session available, login screen is displayed
  */
  async isSession(sessionId:string|null = null) {
    // TODO: use typescript
    return new Promise<any | null>(async (resolve) => {
      const headers = sessionId ? { 'X-SHRIMP-Id': sessionId } : null
      try {
        const res = await this.fetch(`${baseUrl}/isSession`, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
          credentials: 'include',
          headers
        })
        if(res.status === 200) {
          handleNetworkSuccess()
          const session = res.body
          if(session && session.sessionId) {
            resolve(session)
          }
        }
      } catch(err) {
        handleNetworkError(err)
      }
      resolve(null)
    })
  },
  /** test route: authenticate data request with sessionId stored in X-SHRIMP-Id header */
  async test() {
    // TODO: use typescript
    return new Promise<any | null>(async (resolve) => {
      try {
        const res = await this.fetch(`${baseUrl}/test`, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache',
            headers: {
              'X-SHRIMP-Id': sessionStore.session ? sessionStore.session.sessionId : "",
            }
        })

        if(res.status === 200) {
            handleNetworkSuccess()
            const test = await res.body
            resolve(test)
        }
      } catch(err) {
        handleNetworkError(err)
      }
      resolve(null)
    })
  },
  async ping() {
    return new Promise<any | null>(async (resolve) => {
      try {
        const res = await this.fetch(`${baseUrl}/ping`, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache',
            headers: {
              'X-SHRIMP-Id': sessionStore.session ? sessionStore.session.sessionId : "",
            }
        })
        if(res.status === 200) {
            const result = await res.body
            resolve(result)
        }
      } catch(err) {
        resolve(JSON.stringify(err))
      }
      resolve(null)
    })
  },
  getCachedPdf() {
    return new Promise<string[] | null>(async (resolve) => {
      try {
        const res = await this.fetch(`${baseUrl}/getCachedPdf`, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
          headers: {
            'X-SHRIMP-Id': sessionStore.session ? sessionStore.session.sessionId : "",
          }
        })

        if(res.status === 200) {
          handleNetworkSuccess()
          const cachedPdf = await res.body
          resolve(cachedPdf)
        }
      } catch(err) {
        handleNetworkError(err)
      }
      resolve(null)
    })
  },
  /** redirect to the logout route,
   * use hidden input field to transfer the session id to server
   */
  logout() {
    // TODO: use typescript
    const form = document.createElement('form');
    form.method = 'POST'
    form.action = `${baseUrl}/logout`
    // create input field with sessionId
    const hiddenField = document.createElement('input')
    hiddenField.type = 'hidden'
    hiddenField.name = "sessionId"
    hiddenField.value = sessionStore.session ? sessionStore.session.sessionId : ""
    form.appendChild(hiddenField)
    // add input field to html
    document.body.appendChild(form)
    form.submit()
  },

  async fetch(target:string|Request, options:any = {}) {
    const { timeout = env.httpDefaultTimeout, pipeThrough = false, ...fetchOptions } = options;

    if (!fetchOptions.headers) fetchOptions.headers = {}
    fetchOptions.headers['X-SHRIMP-VERSION'] = `${version.version} ${version.key}`
    fetchOptions.headers['X-SHRIMP-CC'] = (typeof window === 'object') ? 'Tab' : 'SW'
    let response: Response
    try {
      response = await fetch(target, {
        signal: AbortSignal.timeout ? AbortSignal.timeout(timeout) : null,
        ...fetchOptions,
      })

      if (pipeThrough) return response as Response

      try {
        const body = await response.json()
        return {
          ok: response.ok,
          status: response.status,
          body: body
        }
      }
      catch(e) {
        return {
          ok: false,
          status: response.status,
        }
      }
    }
    catch(e) {
      console.error('API: Failed to fetch:', e, target)
      const res = {
        ok: false,
        status: 502,
        statusText: 'Could not perform api.fetch(). Reason: ' + e,
        body: undefined
      }
      if (pipeThrough) return new Response(JSON.stringify(res), {
        status: 502,
        statusText: 'Could not perform api.fetch(). Reason: ' + e
      })
      return res
    }
  },

  async createUserBatch(podId: string, userBatch: {login:string, userName:string, password:string}[]) {
    const res = await this.fetch(`${baseUrl}/live/createUserBatch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-SHRIMP-ID': sessionStore.session ? sessionStore.session.sessionId : "",
      },
      body: JSON.stringify({
        podId,
        userBatch
      })
    })
    if (res && res.status === 200) return true
    return false
  },

  async changePassword(oldPassword: string, newPassword: string) {
    const res = await this.fetch(`${baseUrl}/live/changePassword`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-SHRIMP-ID': sessionStore.session ? sessionStore.session.sessionId : "",
      },
      body: JSON.stringify({
        oldPassword,
        newPassword
      })
    })
    if (res.status === 200 && res.body?.tModified) {
      const session = sessionStore.session
      if (session) {
        session.user.tModified = res.body.tModified
        sessionStore.setSession(session)
      }
    }
    return res
  },

  async changeInstantPassword(podId: string, userId: number, newPassword: string) {
    const res = await this.fetch(`${baseUrl}/live/changeInstantPassword`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-SHRIMP-ID': sessionStore.session ? sessionStore.session.sessionId : "",
      },
      body: JSON.stringify({
        podId,
        userId,
        newPassword
      })
    })
    return res
  },

  async deleteUser(podId: string, userId: number) {
    const res = await this.fetch(`${baseUrl}/live/deleteUser`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-SHRIMP-ID': sessionStore.session ? sessionStore.session.sessionId : "",
      },
      body: JSON.stringify({
        podId,
        userId
      })
    })
    return res
  }

}

const handleNetworkError = (err: unknown) => {
  if(navigator.serviceWorker && navigator.serviceWorker.controller) {
    uiStore.setIsOffline(true)
  } else {
    alertStore.push(alert(i18next.t('the changes could not be saved and will be lost at the next startup'), 'error', i18next.t('No Internet connection') ?? '', 'WifiOffIcon'))
  }
  console.warn("Network Error:",err)
}

const handleNetworkSuccess = () => {
  if(navigator.serviceWorker && navigator.serviceWorker.controller) {
    uiStore.setIsOffline(false)
  }
}

export default api